import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatRupiah } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { Calendar as CalIcon, ArrowLeft, Clock, Check } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const TIMES = ["09:00", "10:30", "13:00", "14:30", "16:00", "17:30"];

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [s, setS] = useState(null);
  const [counselors, setCounselors] = useState([]);
  const [counselorId, setCounselorId] = useState("");
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");

  useEffect(() => {
    api.get(`/services/${slug}`).then(r => setS(r.data)).catch(() => navigate("/services"));
    api.get("/counselors").then(r => setCounselors(r.data));
  }, [slug, navigate]);

  if (!s) return <div className="lumina-container py-20 text-center text-[#5C5870]">Memuat…</div>;

  const handleBook = () => {
    if (!date || !time) { toast.error("Pilih tanggal & jam terlebih dahulu"); return; }
    const schedule = `${date.toISOString().split("T")[0]} ${time}${counselorId ? " | " + (counselors.find(c => c.id === counselorId)?.name || "") : ""}`;
    addItem({ item_type: "service", item_id: s.id, title: s.title, price: s.price, image: s.image, schedule });
    toast.success("Sesi ditambahkan ke keranjang");
    navigate("/checkout");
  };

  return (
    <div className="lumina-container py-12" data-testid="service-detail-page">
      <Link to="/services" className="inline-flex items-center gap-2 text-[#5C5870] hover:text-[#6B4BA3] mb-8 text-sm"><ArrowLeft className="w-4 h-4" /> Kembali</Link>
      <div className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <div className="lumina-card overflow-hidden mb-8">
            <img src={s.image} alt={s.title} className="w-full aspect-[16/10] object-cover" />
          </div>
          <span className="lumina-label">{s.type}</span>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#1A1728] mt-3 mb-5 leading-tight">{s.title}</h1>
          <div className="flex items-center gap-4 mb-6 text-sm text-[#5C5870]">
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {s.duration_minutes} menit</span>
          </div>
          <p className="text-[#5C5870] leading-relaxed text-lg mb-8">{s.long_description || s.description}</p>
          <div className="space-y-3">
            {["Dipandu psikolog berlisensi", "Aman & rahasia (kode etik psikologi)", "Tindak lanjut via email"].map(b => (
              <div key={b} className="flex items-center gap-3"><Check className="w-5 h-5 text-[#6B4BA3]" /> <span className="text-[#1A1728]">{b}</span></div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="lumina-card p-7 sticky top-28" data-testid="booking-panel">
            <div className="flex items-baseline gap-2 mb-6">
              <span className="text-[#6B4BA3] font-semibold text-3xl">{formatRupiah(s.price)}</span>
              <span className="text-sm text-[#5C5870]">/ sesi</span>
            </div>

            {s.type === "counseling" && (
              <div className="mb-5">
                <label className="lumina-label block mb-2">Pilih Counselor</label>
                <Select value={counselorId} onValueChange={setCounselorId}>
                  <SelectTrigger data-testid="counselor-select"><SelectValue placeholder="Pilih counselor" /></SelectTrigger>
                  <SelectContent>
                    {counselors.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="mb-5">
              <label className="lumina-label block mb-2"><CalIcon className="w-3 h-3 inline mr-1" /> Pilih Tanggal</label>
              <div className="rounded-2xl border border-[#E2DFED] p-2 bg-white">
                <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} data-testid="booking-calendar" />
              </div>
            </div>

            <div className="mb-6">
              <label className="lumina-label block mb-2">Pilih Jam</label>
              <div className="grid grid-cols-3 gap-2">
                {TIMES.map(t => (
                  <button key={t} onClick={() => setTime(t)} data-testid={`time-${t}`}
                    className={`py-2.5 rounded-xl text-sm font-medium transition ${time === t ? "bg-[#6B4BA3] text-white" : "bg-[#E8F0FA] text-[#6B4BA3] hover:bg-[#dbe7f6]"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={handleBook} className="lumina-btn-primary w-full" data-testid="booking-submit-button">Booking Sekarang</button>
            <p className="text-xs text-[#5C5870] text-center mt-3">Konfirmasi akan dikirim via email</p>
          </div>
        </div>
      </div>
    </div>
  );
}
